"use client";

import {
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";

// Six separate boxes rather than one wide field. A single input is simpler to
// build, but the segmented form is what people have been trained to expect by
// every bank and 2FA screen, and it makes "how many digits am I owed" legible
// without reading a hint.
//
// The behaviours that make or break this control:
//   • paste anywhere fills every box (mail clients hand over "123456", iOS
//     autofill hands over the same),
//   • Backspace in an empty box steps back and clears the previous one,
//   • arrows/Home/End navigate, focus selects so retyping replaces,
//   • only the FIRST box carries autocomplete="one-time-code"; repeating it on
//     all six makes iOS offer the code six times.
//
// Value is fully controlled by the parent (a plain string of 0-6 digits), so
// the parent owns submit-on-complete and clear-on-error.

const LENGTH = 6;

export function CodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  invalid,
  autoFocus,
  id = "login-code",
  label = "6-digit login code",
}: {
  value: string;
  onChange: (next: string) => void;
  /** Fired once the 6th digit lands, whatever the input path (type or paste). */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  id?: string;
  label?: string;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  // Six slots, empty string for "not filled yet". `value` never contains a
  // gap — focus is redirected to the first empty box below, so a digit can
  // only ever be appended or overwritten, never dropped into a hole.
  const slots = () => Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");
  const digits = slots();
  // Kept in a ref so a parent that recreates the callback each render doesn't
  // retrigger the completion effect.
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  });

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const focusAt = useCallback((index: number) => {
    const target = refs.current[Math.min(Math.max(index, 0), LENGTH - 1)];
    target?.focus();
    target?.select();
  }, []);

  const commit = useCallback(
    (next: string, focusIndex: number) => {
      const cleaned = next.replace(/\D/g, "").slice(0, LENGTH);
      onChange(cleaned);
      if (cleaned.length === LENGTH) {
        // Blur so the on-screen keyboard retracts before the request lands.
        refs.current[LENGTH - 1]?.blur();
        onCompleteRef.current?.(cleaned);
      } else {
        focusAt(focusIndex);
      }
    },
    [focusAt, onChange],
  );

  const handleInput = (index: number, raw: string) => {
    const typed = raw.replace(/\D/g, "");
    if (!typed) {
      const chars = slots();
      chars[index] = "";
      commit(chars.join(""), index);
      return;
    }
    // One event can carry several digits: autofill and Android keyboards both
    // deliver the whole code at once. Spread it forward from this box.
    const chars = slots();
    typed.split("").forEach((d, i) => {
      if (index + i < LENGTH) chars[index + i] = d;
    });
    commit(chars.join(""), index + typed.length);
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const chars = slots();
      if (chars[index]) {
        chars[index] = "";
        onChange(chars.join(""));
        focusAt(index);
      } else if (index > 0) {
        chars[index - 1] = "";
        onChange(chars.join(""));
        focusAt(index - 1);
      }
      return;
    }
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(index + 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focusAt(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focusAt(LENGTH - 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LENGTH);
    if (!pasted) return;
    e.preventDefault();
    commit(pasted, pasted.length);
  };

  return (
    <div
      className="lpa-otp"
      role="group"
      aria-label={label}
      data-invalid={invalid ? "true" : undefined}
    >
      {Array.from({ length: LENGTH }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          id={i === 0 ? id : `${id}-${i + 1}`}
          className="lpa-otp-box"
          type="text"
          inputMode="numeric"
          // Only the first box advertises the OTP: repeating it makes iOS
          // surface the same suggestion above every single field.
          autoComplete={i === 0 ? "one-time-code" : "off"}
          // maxLength 1 still lets autofill deliver the whole code as one
          // input event, which handleInput spreads across the row.
          maxLength={LENGTH}
          disabled={disabled}
          value={digits[i]}
          aria-label={`Digit ${i + 1} of ${LENGTH}`}
          aria-invalid={invalid ? true : undefined}
          onChange={(e) => handleInput(i, e.currentTarget.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => {
            // Clicking box 5 of an empty row would otherwise leave holes in
            // the code. Bounce focus to the first unfilled box instead.
            if (i > value.length) {
              focusAt(value.length);
              return;
            }
            e.currentTarget.select();
          }}
        />
      ))}
    </div>
  );
}
